import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Alert,
  Snackbar,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  Upload as UploadIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Euro as EuroIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AccountBalance as AccountBalanceIcon,
  Warning as WarningIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';

// Components
import ResponsiveTable from '../../../components/common/ResponsiveTable';
import PageHeader from '../../../components/common/PageHeader';
import SearchHeader from '../../../components/common/SearchHeader';
import MetricsCard from '../../../components/common/MetricsCard';
import PaginationComponent from '../../../components/common/PaginationComponent';
import ItemCard from '../../../components/common/ItemCard';
import InvoiceFormModal from '../components/InvoiceFormModal';
import PaymentUpdateModal from '../components/PaymentUpdateModal';
import InvoicePreviewModal from '../components/InvoicePreviewModal';

// Services
import invoiceService from '../services/invoiceService';
import APIClient from '../../../lib/api';

// Utils
import { formatCurrency } from '../../../utils/formatters';

const InvoiceListingPage = () => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
    totalPages: 0
  });

  // Filters
  const [filters, setFilters] = useState({
    client_id: null,
    invoice_type: null,
    payment_status: null,
    date_from: null,
    date_to: null,
    search: '',
    sort_by: 'issue_date',
    sort_order: 'desc',
    exclude_proforma: true
  });

  const [viewMode, setViewMode] = useState('invoices');

  // Modals
  const [invoiceFormModal, setInvoiceFormModal] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [modalMode, setModalMode] = useState('create'); // 'create', 'edit', 'createFromServices'
  const [previewOpen, setPreviewOpen] = useState(false);

  // Menu
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  // Statistics
  const [statistics, setStatistics] = useState({
    total_invoices: 0,
    total_amount: 0,
    paid_amount: 0,
    outstanding_amount: 0,
    overdue_count: 0,
    unpaid_count: 0,
    partial_count: 0,
    paid_count: 0
  });


  // Load data
  const loadInvoices = async () => {
    try {
      setLoading(true);
      const response = await invoiceService.getInvoices({
        ...filters,
        page: pagination.page,
        limit: pagination.limit
      });
      
      const invoices = Array.isArray(response) ? response : [];
      setInvoices(invoices);
      setPagination(prev => ({
        ...prev,
        total: invoices.length,
        totalPages: Math.ceil(invoices.length / pagination.limit)
      }));
    } catch (err) {
      setError('Σφάλμα κατά τη φόρτωση των τιμολογίων');
      console.error('Error loading invoices:', err);
      setInvoices([]);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await invoiceService.getGreekInvoiceStatistics();
      setStatistics(stats || {
        total_invoices: 0,
        total_amount: 0,
        paid_count: 0,
        overdue_count: 0,
        partial_count: 0,
        paid_count: 0
      });
    } catch (err) {
      console.error('Error loading statistics:', err);
      setStatistics({
        total_invoices: 0,
        total_amount: 0,
        paid_count: 0,
        overdue_count: 0,
        partial_count: 0,
        paid_count: 0
      });
    }
  };

  useEffect(() => {
    loadInvoices();
    loadStatistics();
  }, [filters, pagination.page]);

  // Handlers
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleCreateFromServices = () => {
    setSelectedInvoice(null);
    setModalMode('createFromServices');
    setInvoiceFormModal(true);
    handleMenuClose();
  };

  const handleCreateInvoice = () => {
    setSelectedInvoice(null);
    setModalMode('create');
    setInvoiceFormModal(true);
    handleMenuClose();
  };

  const handleEditInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setModalMode('edit');
    setInvoiceFormModal(true);
    handleMenuClose();
  };

  const handleUpdatePayment = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentModal(true);
    handleMenuClose();
  };

  const handleMyDataUpload = async (invoice) => {
    if (!invoice) return;
    
    try {
      setLoading(true);
      setError(null); // Clear previous errors
      
      // Get MyData settings to use the saved preference
      const settingsResult = await APIClient.settings.getMyData();
      const useTestApi = settingsResult.data?.use_test_api ?? true; // Default to dev
      
      await invoiceService.uploadToMyData(invoice.id, {
        company_vat: '148060098',
        use_test_api: useTestApi
      });
      setSuccess('Το τιμολόγιο ανέβηκε στο MyDATA επιτυχώς');
      loadInvoices();
      loadStatistics();
    } catch (err) {
      // Extract error message from various possible locations
      const errorMessage = 
        err.response?.data?.detail || 
        err.response?.data?.message || 
        err.response?.data?.error ||
        err.message ||
        'Σφάλμα κατά την αποστολή στο MyDATA';
      setError(errorMessage);
      console.error('Error uploading to MyDATA:', err);
    } finally {
      setLoading(false);
    }
    handleMenuClose();
  };

  const handleDeleteInvoice = async (invoice) => {
    try {
      const info = await invoiceService.getInvoiceDeleteInfo(invoice.id);
      if (!info.can_delete) {
        setError(info.reason || 'Δεν μπορεί να διαγραφεί το τιμολόγιο');
        handleMenuClose();
        return;
      }
      const msg = info.credit_notes_count > 0
        ? `Αυτό το τιμολόγιο έχει ${info.credit_notes_count} πιστωτικό/ά. Θα διαγραφούν και αυτά. Συνέχεια;`
        : 'Είστε σίγουροι ότι θέλετε να διαγράψετε αυτό το τιμολόγιο;';
      if (!window.confirm(msg)) {
        handleMenuClose();
        return;
      }
      await invoiceService.deleteInvoice(invoice.id);
      setSuccess('Το τιμολόγιο διαγράφηκε επιτυχώς');
      loadInvoices();
      loadStatistics();
    } catch (err) {
      setError(err.response?.data?.detail || 'Σφάλμα κατά τη διαγραφή του τιμολογίου');
      console.error('Error deleting invoice:', err);
    }
    handleMenuClose();
  };

  const handleDownloadPDF = async (invoice) => {
    try {
      const pdfBlob = await invoiceService.generateInvoicePDF(invoice.id);
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Invoice_${invoice.invoice_number}_${invoiceService.formatDate(invoice.issue_date)}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      setSuccess('Το PDF κατέβηκε επιτυχώς');
    } catch (err) {
      setError('Σφάλμα κατά τη λήψη του PDF');
      console.error('Error downloading PDF:', err);
    }
    handleMenuClose();
  };

  const handleRefresh = () => {
    loadInvoices();
    loadStatistics();
  };

  const handleSort = (field, direction) => {
    handleFilterChange({ sort_by: field, sort_order: direction });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleViewModeChange = (event, newMode) => {
    if (!newMode) return;
    setViewMode(newMode);
    if (newMode === 'proforma') {
      handleFilterChange({ invoice_type: 'proforma_invoice', exclude_proforma: false });
    } else {
      handleFilterChange({ invoice_type: null, exclude_proforma: true });
    }
  };

  // Mobile card renderer
  const renderInvoiceCard = (invoice) => {
    const typeInfo = invoiceService.getInvoiceTypeDisplay(invoice.invoice_type, invoice, true);
    const paid = invoice.paid_amount ?? 0;
    const total = invoice.total_amount ?? 0;

    // Format payment status
    let paymentStatusLabel = 'Ανεξόφλητο';
    let paymentStatusColor = 'error';
    if (paid >= total) {
      paymentStatusLabel = 'Εξοφλημένο';
      paymentStatusColor = 'success';
    } else if (paid > 0) {
      paymentStatusLabel = 'Μερική';
      paymentStatusColor = 'warning';
    }

    return (
      <ItemCard
        key={invoice.id}
        title={invoice.invoice_number}
        subtitle={null}
        headerActions={[
          <Chip 
            key="type"
            label={typeInfo.text}
            color={typeInfo.color}
            size="small"
          />,
          <Chip 
            key="payment"
            label={paymentStatusLabel}
            color={paymentStatusColor}
            variant="outlined"
            size="small"
          />,
          invoice.mydata_mark && (
            <Chip 
              key="mydata"
              label="MyDATA"
              color="success"
              variant="outlined"
              size="small"
              icon={<CheckCircleIcon fontSize="small" />}
            />
          )
        ].filter(Boolean)}
        basicInfo={[
          // Σύνολο
          {
            icon: <EuroIcon fontSize="small" color="action" />,
            text: `Σύνολο: ${invoiceService.formatCurrency(total)}`
          },
          // Πελάτης
          {
            icon: <PersonIcon fontSize="small" color="action" />,
            text: invoice.client?.name || 'N/A'
          },
          // Ημ. Έκδοσης
          {
            icon: <CalendarIcon fontSize="small" color="action" />,
            text: `Ημ. Έκδοσης: ${invoiceService.formatDate(invoice.issue_date)}`
          },
          // Εξοφλημένο
          {
            icon: <AccountBalanceIcon fontSize="small" color={paid >= total ? 'success' : 'warning'} />,
            text: `Εξοφλημένο: ${invoiceService.formatCurrency(paid)}`
          },
          // MyDATA status
          {
            icon: invoice.mydata_mark ? <CheckCircleIcon fontSize="small" color="success" /> : <WarningIcon fontSize="small" color="warning" />,
            text: invoice.mydata_mark ? `MyDATA: ${invoice.mydata_mark.substring(0, 15)}...` : 'Δεν έχει ανέβει στο MyDATA'
          }
        ]}
        actions={[
          <IconButton
            key="view"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedInvoice(invoice);
              setPreviewOpen(true);
            }}
            sx={{ color: 'info.main' }}
            title="Προβολή"
          >
            <ViewIcon fontSize="small" />
          </IconButton>,
          <IconButton
            key="edit"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleEditInvoice(invoice);
            }}
            sx={{ color: 'primary.main' }}
            title="Επεξεργασία"
          >
            <EditIcon fontSize="small" />
          </IconButton>,
          <IconButton
            key="menu"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleMenuOpen(e, invoice);
            }}
            sx={{ color: 'text.secondary' }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ]}
      />
    );
  };

  // Table columns
  const columns = [
    {
      field: 'invoice_number',
      headerName: 'Αριθμός',
      sortable: true,
      renderCell: (value, row) => (
        <Typography variant="body2" fontWeight="medium">
          {row.invoice_number}
        </Typography>
      )
    },
    {
      field: 'client',
      headerName: 'Πελάτης',
      sortable: true,
      sortField: 'client_id', // Use client_id for backend sorting
      minWidth: 150,
      renderCell: (value, row) => (
        <Typography variant="body2">
          {row.client?.name || 'N/A'}
        </Typography>
      )
    },
    {
      field: 'invoice_type',
      headerName: 'Τύπος',
      sortable: true,
      minWidth: 120,
      renderCell: (value, row) => {
        const typeInfo = invoiceService.getInvoiceTypeDisplay(row.invoice_type, row, true);
        return (
          <Chip
            label={typeInfo.text}
            color={typeInfo.color}
            size="small"
            variant="outlined"
          />
        );
      }
    },
    {
      field: 'total_amount',
      headerName: 'Σύνολο',
      sortable: true,
      minWidth: 150,
      renderCell: (value, row) => {
        const total = row.total_amount ?? 0;
        const vat = row.vat_amount ?? 0;
        const withholding = row.withholding_tax;
        
        return (
          <Box>
            <Typography variant="body2" fontWeight="bold" color="text.primary">
              Σύνολο: {invoiceService.formatCurrency(total)}
            </Typography>
            <Typography variant="caption" color="error" display="block">
              ΦΠΑ: {invoiceService.formatCurrency(vat)}
            </Typography>
            {withholding && parseFloat(withholding) > 0 && (
              <Typography variant="caption" color="success.main" display="block">
                Παρακράτηση: {invoiceService.formatCurrency(withholding)}
              </Typography>
            )}
          </Box>
        );
      }
    },
    {
      field: 'mydata_mark',
      headerName: 'MyDATA',
      sortable: false,
      minWidth: 120,
      renderCell: (value, row) => {
        if (row.mydata_mark) {
          return (
            <Tooltip title={`MARK: ${row.mydata_mark}`}>
              <Chip
                label="Ανέβηκε"
                color="success"
                size="small"
                icon={<CheckCircleIcon />}
              />
            </Tooltip>
          );
        }
        // Προτιμολόγια δεν μπορούν να ανέβουν στο MyDATA
        if (row.invoice_type === 'proforma_invoice') {
          return (
            <Tooltip title="Τα προτιμολόγια δεν ανέρχονται στο MyDATA">
              <Chip
                label="N/A"
                size="small"
                variant="outlined"
                disabled
              />
            </Tooltip>
          );
        }
        
        return (
          <Tooltip title="Δεν έχει ανέβει στο MyDATA">
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<UploadIcon />}
              onClick={(e) => {
                e.stopPropagation();
                handleMyDataUpload(row);
              }}
            >
              Upload
            </Button>
          </Tooltip>
        );
      }
    },
    {
      field: 'issue_date',
      headerName: 'Ημ. Έκδοσης',
      sortable: true,
      minWidth: 150,
      renderCell: (value, row) => (
        <Typography variant="body2">
          {invoiceService.formatDate(row.issue_date)}
        </Typography>
      )
    },
    {
      field: 'actions',
      headerName: 'Ενέργειες',
      sortable: false,
      minWidth: 100,
      renderCell: (value, row) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleMenuOpen(e, row);
          }}
        >
          <MoreVertIcon />
        </IconButton>
      )
    }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Page Header */}
      <PageHeader
        title="Τιμολόγια"
        subtitle="Διαχείριση τιμολογίων και αποδείξεων"
        actions={[
          <Button
            key="create-invoice"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateFromServices}
          >
            Δημιουργία Τιμολογίου
                      </Button>
        ]}
      />

      {/* Statistics Cards */}
      <Grid container spacing={isXs ? 1 : 2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricsCard
            title="Σύνολο Τιμολογίων"
            value={statistics.total_invoices}
            color="primary"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricsCard
            title="Συνολικό Ποσό"
            value={invoiceService.formatCurrency(statistics.total_amount)}
            color="info"
          />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <MetricsCard
            title="Εξοφλημένο"
            value={invoiceService.formatCurrency(statistics.paid_amount)}
            color="success"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <MetricsCard
            title="Εκκρεμές"
            value={invoiceService.formatCurrency(statistics.outstanding_amount)}
            color="warning"
          />
        </Grid>
        <Grid item xs={6} sm={6} md={2.4}>
          <MetricsCard
            title="Ληξιπρόθεσμα"
            value={statistics.overdue_count}
            color="error"
          />
        </Grid>
      </Grid>

      {/* Search and Filters */}
      <SearchHeader
        searchValue={filters.search || ''}
        onSearchChange={(search) => handleFilterChange({ search })}
        searchPlaceholder="Αναζήτηση τιμολογίων..."
        sortField={filters.sort_by}
        sortDirection={filters.sort_order}
        onSortChange={handleSort}
        sortOptions={[
          { value: 'issue_date', label: 'Ημ. Έκδοσης' },
          { value: 'total_amount', label: 'Σύνολο' },
          { value: 'invoice_number', label: 'Αριθμός' },
          { value: 'created_at', label: 'Ημ. Δημιουργίας' },
        ]}
        actions={[
          <ToggleButtonGroup
            key="view-mode"
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
          >
            <ToggleButton value="invoices">Τιμολόγια</ToggleButton>
            <ToggleButton value="proforma">Προτιμολόγια</ToggleButton>
          </ToggleButtonGroup>
        ]}
      />

      {/* Data Table */}
      <ResponsiveTable
        data={invoices}
        columns={columns}
        loading={loading}
        emptyMessage="Δεν βρέθηκαν τιμολόγια"
        sortField={filters.sort_by}
        sortDirection={filters.sort_order}
        onSort={handleSort}
        renderMobileCard={renderInvoiceCard}
        onRowClick={(row) => {
          setSelectedInvoice(row);
          setPreviewOpen(true);
        }}
      />

      {/* Pagination */}
      <PaginationComponent
        currentPage={pagination.page}
        totalPages={pagination.totalPages}
        totalItems={pagination.total}
        itemsPerPage={pagination.limit}
        onPageChange={handlePageChange}
        onItemsPerPageChange={(newLimit) => {
          setPagination(prev => ({ ...prev, limit: newLimit, page: 1 }));
        }}
      />

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => handleEditInvoice(selectedRow)}>
          <EditIcon sx={{ mr: 1 }} />
          Επεξεργασία
        </MenuItem>
        <MenuItem onClick={() => handleUpdatePayment(selectedRow)}>
          <PaymentIcon sx={{ mr: 1 }} />
          Πληρωμή
        </MenuItem>
        {!selectedRow?.mydata_mark && selectedRow?.invoice_type !== 'proforma_invoice' && (
          <MenuItem onClick={() => handleMyDataUpload(selectedRow)}>
            <UploadIcon sx={{ mr: 1 }} />
            Upload MyDATA
          </MenuItem>
        )}
        {selectedRow?.mydata_mark && (
          <MenuItem disabled>
            <CheckCircleIcon sx={{ mr: 1 }} color="success" />
            MyDATA: {selectedRow.mydata_mark.substring(0, 10)}...
          </MenuItem>
        )}
        <MenuItem onClick={() => handleDownloadPDF(selectedRow)}>
          <DownloadIcon sx={{ mr: 1 }} />
          PDF
        </MenuItem>
        <MenuItem onClick={() => handleDeleteInvoice(selectedRow)}>
          <DeleteIcon sx={{ mr: 1 }} />
          Διαγραφή
        </MenuItem>
      </Menu>

      {/* Modals */}
      <InvoiceFormModal
        open={invoiceFormModal}
        onClose={() => setInvoiceFormModal(false)}
        invoice={selectedInvoice}
        mode={modalMode}
        onSuccess={() => {
          setInvoiceFormModal(false);
          loadInvoices();
          loadStatistics();
          const successMessage = modalMode === 'edit' 
            ? 'Το τιμολόγιο ενημερώθηκε επιτυχώς'
            : 'Το τιμολόγιο δημιουργήθηκε επιτυχώς';
          setSuccess(successMessage);
        }}
      />

      <InvoicePreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        invoiceId={selectedInvoice?.id}
      />

      <PaymentUpdateModal
        open={paymentModal}
        onClose={() => setPaymentModal(false)}
        invoice={selectedInvoice}
        onSuccess={() => {
          setPaymentModal(false);
          loadInvoices();
          loadStatistics();
          setSuccess('Η πληρωμή ενημερώθηκε επιτυχώς');
        }}
      />

      {/* Notifications */}
      <Snackbar
        open={Boolean(error)}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(success)}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvoiceListingPage;
